import { parse, format, add } from 'date-fns';
import { costFormatter } from '../../format';
import { interpolate } from '../../cms/index.js';
import { getRecurringOrdersProps } from '../constants';
import { interpolations } from './interpolation.js';

export const isDisabled = ({ isValid, isSubmitting, isSubmitted, errors }) =>
  !isValid || isSubmitting || isSubmitted || errors.address !== undefined;

export const getCheckoutBarProps = ({
  cart,
  formState,
  ctaText,
  refInView,
}) => {
  const shown = !refInView;
  const disabled = isDisabled(formState);
  const period = 'week';

  let amount = '';
  let discountedAmount = '';
  if (cart) {
    const amountPerWeek = cart.line_items.reduce(
      (prev, current) => prev + Number(current.price_per_week),
      0,
    );
    const amountPerWeekDiscount = cart.line_items.reduce(
      (prev, current) => prev + Number(current.discounted_price_per_week),
      0,
    );
    amount = costFormatter(amountPerWeek);
    discountedAmount = costFormatter(amountPerWeekDiscount);
  }

  return {
    ctaText,
    shown,
    disabled,
    amount,
    discountedAmount,
    period,
    isSubmitting: formState.isSubmitting,
  };
};

export const getCheckoutInformationProps = ({
  refreshCart,
  isValidating,
  cart,
  deletePet,
  pets,
  address,
  products,
  subscriptionProducts,
  formState,
  orderSummary,
  formattedOfferAmount,
  recurringOrders,
  buttonRef,
}) => {
  const onClickRemovePet = async petId => {
    await deletePet(petId);
    refreshCart(petId);
  };
  let plans = [];
  let orderSummaryProps = { ...orderSummary, onClickRemovePet };
  let recurringOrdersProps = { ...recurringOrders };
  let disabled = isDisabled(formState);
  let nextDeliveryDate;
  let recurringDeliveryDate;

  if (cart && products) {
    orderSummaryProps.pricingList = {
      ...orderSummaryProps.pricingList,
      total: cart.total,
      subtotal: cart.subtotal,
      discountText: `${formattedOfferAmount} off`,
      discountAmount: cart.total_discount,
      tax: cart.total_tax !== '0.00' ? cart.total_tax : '',
    };
    plans = pets.map(({ id, name, breeds, gender }) => {
      const line_item = cart.line_items.find(item => item.pet_id === id);
      const petId = line_item.pet_id;
      const product = products.find(
        product => product.variants[0].sku === line_item.sku,
      );
      return {
        petId,
        petName: name,
        breeds: breeds.map(({ name }) => name),
        frequency: product.weeks_of_food,
        numPacks: Number(product.variants[0].tray_count),
        bagCount: Number(product.variants[0].bag_count),
        productType: product.product_type,
        price: line_item.price,
        priceWithDiscount: String(line_item.discounted_price),
        pricePerWeekWithDiscount: String(line_item.discounted_price_per_week),
        pricePerWeek: line_item.price_per_week,
        recipes: product.variants[0].recipes,
        gender,
      };
    });
  }

  if (pets && cart && subscriptionProducts) {
    recurringOrdersProps.foodPacksText = pets
      .map(pet => {
        const line_item = cart.line_items.find(item => item.pet_id === pet.id);
        const product = products.find(
          product => product.variants[0].sku === line_item.sku,
        );

        return interpolate(
          getRecurringOrdersProps(product.product_type).foodPacksText,
          interpolations(pet, cart, subscriptionProducts),
        );
      })
      .join(' ');
  } else {
    recurringOrdersProps.foodPacksPreface = '';
    recurringOrdersProps.foodPacksText = '';
  }

  if (cart?.delivery_date) {
    nextDeliveryDate = format(
      parse(cart.delivery_date, 'yyyy-MM-dd', new Date()),
      'EE, MMM d',
    );
  }

  if (pets && address) {
    const smallestTrialFrequency = Math.min(
      ...pets.map(({ trial_plan }) => trial_plan?.frequency),
    );
    recurringDeliveryDate =
      !address.isFallback && address.next_delivery_date
        ? format(
            add(new Date(address.next_delivery_date), {
              weeks: smallestTrialFrequency,
            }),
            'MM/dd/yyyy',
          )
        : '';
  }

  return {
    plans,
    nextDeliveryDate,
    recurringDeliveryDate,
    orderSummary: orderSummaryProps,
    recurringOrders: recurringOrdersProps,
    isValidating,
    isSubmitting: formState.isSubmitting,
    disabled,
    buttonRef,
  };
};
